<!-- eslint-disable max-len -->
<style scoped>
  .project-outcome-summary-card-body {
    padding-right: 0 !important;
    padding-left: 0 !important;
  }
  .project-outcome-summary-card-header {
    background-color: #D5D5D5;
    border-bottom: 1px solid rgba(0, 0, 0, 0.125);
    margin-bottom:0;
    padding-top: 0.75rem;
    padding-right: 1.25rem;
    padding-bottom: 0.75rem;
    padding-left: 1.25rem;
  }
  .project-outcome-summary-card-text {
    background-color: #FFFFFF !important;
  }
</style>
<template>
  <div class='view-contents'>
    <UnavailablePageModal
      v-if="project"
      pageName="Project Outcomes"
      :project="project"
      :projectId="project.id"
      :unallowedStatuses="unallowedStatuses"
    />
    <b-alert
      :show="dismissOutcomeSummarySaveSuccessAlert"
      dismissible
      variant="success"
      @dismissed="dismissOutcomeSummarySaveSuccessAlert=0"
      @dismiss-count-down="dismissOutcomeSummarySaveSuccessAlertChanged"
    >
      <span>Outcome Summary was successfully saved</span>
    </b-alert>
    <b-modal
      id="outcomeSummaryEditModal"
      size="lg"
      @ok="saveOutcomeSummary()"
    >
      <template v-slot:modal-title>Edit Outcome Summary</template>
      <VueFormGenerator
        :model="outcomeSummaryFormModel"
        :schema="outcomeSummaryFormSchema"
        :options="outcomeSummaryFormOptions"
      />
      <template v-slot:modal-footer="{ ok, cancel }">
        <b-button size="sm" variant="danger" @click="cancel" class='btn-danger'>Cancel</b-button>
        <b-button size="sm" variant="success" @click="ok" class='btn-success'>Save</b-button>
      </template>
    </b-modal>
    <b-modal
      v-if='config'
      id="projectTargetMonitoringEventAddModal"
      size="lg"
      @ok="saveProjectTargetMonitoringEventAddModel()"
    >
      <template v-slot:modal-title>Add Monitoring Event</template>
      <VueFormGenerator
        :model="projectTargetMonitoringEventAddModel"
        :schema="projectTargetMonitoringEventAddSchema"
        :options="projectTargetMonitoringEventAddFormOptions"
        @validated="onProjectTargetMonitoringEventAddFormValidated"
        @model-updated='onProjectTargetMonitoringEventAddFormModelUpdated'
      />
      <template v-slot:modal-footer="{ ok, cancel }">
        <b-button size="sm" variant="danger" @click="cancel" class='btn-danger'>Cancel</b-button>
        <b-button size="sm" variant="success" @click="ok" class='btn-success' :disabled='!projectTargetMonitoringEventAddModelValid'>Save</b-button>
      </template>
    </b-modal>
    <b-modal id="targetMonitoringEventDeleteModal" title="Confirm Delete" @ok="onProjectTargetMonitoringEventDeleteOkClick()">
      <div>Are you sure you want to delete this Monitoring Event?</div>
      <template v-slot:modal-footer="{ ok, cancel }">
        <b-button size="sm" variant="danger" @click="cancel" class='incentive-payments-dialog-no'>No</b-button>
        <b-button size="sm" variant="success" @click="ok" class='incentive-payments-dialog-yes'>Yes</b-button>
      </template>
    </b-modal>
    <div class="section-header">
      <div class="row bold-title">
        <div class="col">Outcomes</div>
      </div>
    </div>
    <b-overlay :show="formLoading">
      <div class='card-body project-outcome-summary-card-body'>
        <div class='project-outcome-summary-card-header'>
          <b-row>
            <b-col cols='6'>
              <span class='details-label'>Outcome Summary</span>
              <a v-b-toggle="`outcomeSummaryCard`" class="c-pointer">
                <b-icon icon='caret-right-fill' class="when-closed" />
                <b-icon icon='caret-down-fill' class="when-opened" />
              </a>
            </b-col>
            <b-col cols='6' class='text-right'>
              <span v-b-tooltip.hover title='Edit this Outcome Summary'>
                <b-link class='project-outcome-summary-edit text-right' @click='onOutcomeSummaryEditClick()'/>
              </span>
            </b-col>
          </b-row>
        </div>
        <b-collapse :id="`outcomeSummaryCard`" class='card' visible>
          <b-form-textarea
            v-if='project && project.outcomeSummary'
            v-model='project.outcomeSummary'
            class='project-outcome-summary-card-text'
            readonly
            rows='4'
        />
        </b-collapse>
      </div>
      <div v-for="projectTarget in projectTargets" :key="projectTarget.targetId" class="card section-header-no-line">
        <div class='card-body'>
          <b-row class='details-row'>
            <b-col>
              <span class='bold-title details-label'>Target:&nbsp;</span><span>{{ projectTarget.target.name }}</span>
            </b-col>
            <b-col cols='4' class='text-right'>
              <b-link class="add-link"
                v-if='config'
                @click='onProjectTargetMonitoringEventAddClick(projectTarget)'
                v-b-tooltip.hover title='Add a new Monitoring Event to this Target'
              >
                <b-icon icon="plus"></b-icon>
                <span>Add Monitoring Event</span>
              </b-link>
            </b-col>
          </b-row>
          <b-row>
            <b-col><span class='details-label'>Project Objective(s):</span></b-col>
          </b-row>
          <div>
            <ul>
              <li v-for='objective in projectTarget.objectives' :key="objective.id">{{ objective.name }}</li>
            </ul>
          </div>
          <div v-if='projectTarget.monitoringEvents.length === 0'>No Monitoring Events have been recorded.</div>
          <div v-else>
            <b-table
              :ref='`targetMonitoringEventsTable${projectTarget.targetId}`'
              :fields="getProjectTargetMonitoringEventFields(projectTarget.targetId)"
              :items="projectTarget.monitoringEvents"
              bordered
              hover
              small
              striped
            >
              <template v-slot:cell(actions)="row">
                <b-row>
                  <b-col>
                    <b-link v-b-modal="'targetMonitoringEventDeleteModal'" @click='onProjectTargetMonitoringEventDeleteClick(row.item)' v-b-tooltip.hover title='Delete this Monitoring Event'>
                      <div class='project-workorder-lineitem-delete'></div>
                    </b-link>
                  </b-col>
                </b-row>
              </template>
              <template v-slot:cell(monitoringEventUrl)="row">
                <b-row>
                  <b-col>
                    <b-link :href='row.item.monitoringEventUrl' target='_blank' class='font-weight-bold'>{{row.item.monitoringEventUrl}}</b-link>
                  </b-col>
                </b-row>
              </template>
            </b-table>
          </div>
        </div>
      </div>
    </b-overlay>
  </div>
</template>
<script>
/* eslint-disable max-len, padded-blocks, prefer-destructuring */
import { mapState } from 'vuex';
import UnavailablePageModal from '@/components/common/UnavailablePageModal.vue';
import ProjectSchemaHelper from '../../mixins/projectSchemaHelper';

export default {
  name: 'ProjectOutcomes',
  mixins: [ProjectSchemaHelper],
  components: {
    UnavailablePageModal,
  },
  computed: {
    ...mapState(['config']),
    ...mapState('Project', {
      project: (e) => e.project,
      projectObjectives: (e) => e.projectObjectives,
      projectObjectiveAccomplishmentTargetSelections: (e) => e.projectObjectiveAccomplishmentTargetSelections,
      projectAccomplishmentTargetMonitoringEvents: (e) => e.projectAccomplishmentTargetMonitoringEvents,
      projectAccomplishmentTargetMonitoringEventEvaluationMethods: (e) => e.projectAccomplishmentTargetMonitoringEventEvaluationMethods,
    }),
  },
  data() {
    return {
      dismissOutcomeSummarySaveSuccessAlert: 0,
      formLoading: true,
      outcomeSummaryFormModel: null,
      outcomeSummaryFormOptions: {
        validateAfterLoad: false,
        validateAfterChanged: true,
        validateAsync: true,
      },
      outcomeSummaryFormSchema: null,
      projectTargetMonitoringEventAddFormOptions: {
        validateAfterLoad: false,
        validateAfterChanged: true,
        validateAsync: true,
      },
      projectTargetMonitoringEventAddModel: null,
      projectTargetMonitoringEventAddModelEvaluationMethods: [],
      projectTargetMonitoringEventAddModelPlantTypes: [],
      projectTargetMonitoringEventAddModelValid: false,
      projectTargetMonitoringEventAddSchema: null,
      projectTargetMonitoringEventDeleteModel: null,
      projectTargetMonitoringEventFields: [
        {
          key: 'monitoringEventDate',
          label: 'Monitoring Date',
          formatter: (value) => {
            if (value) {
              return new Date(value).toLocaleDateString();
            }
            return '';
          },
          thClass: 'workorder-table-header project-outcomes-date-header',
        },
        {
          key: 'monitoringEventEvaluationMethod',
          label: 'Evaluation Method',
          formatter: (value) => {
            if (value) {
              return value;
            }
            return '';
          },
          thClass: 'workorder-table-header',
        },
        {
          key: 'monitoringEventEvaluation',
          label: 'Evaluation',
          formatter: (value) => {
            if (value) {
              return 'Yes';
            }
            return 'No';
          },
          thClass: 'workorder-table-header',
        },
        {
          key: 'monitoringEventUnits',
          label: 'Units',
          formatter: (value) => {
            if (value) {
              return value;
            }
            return '';
          },
          thClass: 'workorder-table-header',
        },
        {
          key: 'monitoringEventPlantType',
          label: 'Plant Type',
          formatter: (value) => {
            if (value) {
              return value;
            }
            return '';
          },
          thClass: 'workorder-table-header',
        },
        {
          key: 'monitoringEventPhotoMonitored',
          label: 'Photo Monitored',
          formatter: (value) => {
            if (value) {
              return 'Yes';
            }
            return 'No';
          },
          thClass: 'workorder-table-header project-outcomes-photo-monitored-header',
        },
        {
          key: 'monitoringEventUrl',
          label: 'Link',
          formatter: (value) => {
            if (value) {
              return value;
            }
            return '';
          },
          thClass: 'workorder-table-header',
        },
        {
          key: 'actions',
          label: '',
          thClass: 'workorder-table-header project-outcomes-action-header',
        },
      ],
      projectTargets: null,
      unallowedStatuses: [],
    };
  },
  async mounted() {
    this.formLoading = true;

    this.projectId = parseInt(this.$route.params.id, 10);

    await this.$store.dispatch('fetchConfiguration');
    this.unallowedStatuses = [
      this.config.projectOverviewPage.submitStatus.Draft,
      this.config.projectOverviewPage.submitStatus.SubmittedForPlanningApproval,
      this.config.projectOverviewPage.submitStatus.ApprovedForPlanning,
      this.config.projectOverviewPage.submitStatus.ConceptRejected,
      this.config.projectOverviewPage.submitStatus.SubmittedForProjectApproval,
      this.config.projectOverviewPage.submitStatus.Rejected,
    ];
    await this.$store.dispatch('Project/getProject', this.projectId);
    await this.$store.dispatch('Project/getProjectObjectivesAsync');
    await this.$store.dispatch('Project/getProjectAccomplishmentTargetMonitoringEventEvaluationMethods');

    await this.loadProjectTargets();

    this.formLoading = false;
  },
  methods: {
    dismissOutcomeSummarySaveSuccessAlertChanged(value) {
      this.dismissOutcomeSummarySaveSuccessAlert = value;
    },
    getProjectTargetMonitoringEventFields(targetId) {

      const items = (this.projectAccomplishmentTargetMonitoringEvents || []).filter(
        (e) => e.projectAccomplishmentTargetId === targetId && e.monitoringEventPlantType !== null,
      );

      let fields = [...this.projectTargetMonitoringEventFields];

      if (items.length === 0) {
        fields = fields.filter((e) => e.key !== 'monitoringEventPlantType');
      }

      return fields;
    },
    initializeOutcomeSummaryFormModel() {
      this.outcomeSummaryFormModel = {
        id: this.project.id,
        outcomeSummary: this.project.outcomeSummary,
      };
    },
    initializeProjectTargetMonitoringEventAddModel(projectTarget) {
      this.projectTargetMonitoringEventAddModelPlantTypes = this.config.projectOutcome.monitoringEventPlantTypes;
      this.projectTargetMonitoringEventAddModel = {
        projectId: this.projectId,
        projectAccomplishmentTargetId: projectTarget.targetId,
        monitoringEventDate: new Date(),
        monitoringEventEvaluationMethod: null,
        monitoringEventEvaluationMethodType: null,
        monitoringEventEvaluationMethodUnit: null,
        monitoringEventEvaluationBit: null,
        monitoringEventEvaluationNumber: 0,
        monitoringEventEvaluationText: null,
        monitoringEventPlantType: null,
        monitoringEventPhotoMonitored: false,
        monitoringEventUrl: null,
      };
    },
    async loadProjectTargets() {

      await this.$store.dispatch('Project/getProjectObjectiveAccomplishmentTargetSelectionsAsync', this.project.id);
      await this.$store.dispatch('Project/getProjectAccomplishmentTargetMonitoringEvents', this.project.id);

      const projectObjectiveTargets = this.projectObjectives.map(
        (e) => e.projectObjectiveAccomplishmentTargets,
      ).flat().map(
        (e) => e.projectAccomplishmentTarget,
      );

      const projectTargets = this.projectObjectiveAccomplishmentTargetSelections.map(
        (e) => {
          const projectTarget = {
            targetId: e.projectAccomplishmentTargetId,
            target: projectObjectiveTargets.find((target) => target.id === e.projectAccomplishmentTargetId),
            objectiveId: e.projectObjectiveId,
          };
          projectTarget.objective = this.projectObjectives.find(
            (objective) => objective.id === projectTarget.objectiveId,
          );
          return projectTarget;
        },
      );

      const projectTargetsMap = new Map();

      projectTargets.forEach((e) => {
        const key = `target_${e.targetId}`;
        const value = projectTargetsMap.get(key);
        if (!value) {
          projectTargetsMap.set(key, { targetId: e.targetId, target: e.target, objectives: [e.objective] });
        } else {
          value.objectives.push(e.objective);
        }
      });

      /* eslint-disable-next-line */
      this.projectTargets = Array.from(projectTargetsMap, ([name, value]) => (value));

      this.projectAccomplishmentTargetMonitoringEvents.forEach((e) => {
        const evaluationMethod = this.projectAccomplishmentTargetMonitoringEventEvaluationMethods.find((method) => method.id === e.monitoringEventEvaluationMethodId);
        e.monitoringEventEvaluationMethod = evaluationMethod.name;
        switch (evaluationMethod.type) {
          case 'Bit':
            e.monitoringEventEvaluation = e.monitoringEventEvaluationBit;
            break;
          case 'Number':
            e.monitoringEventEvaluation = e.monitoringEventEvaluationNumber;
            break;
          case 'Text':
            e.monitoringEventEvaluation = e.monitoringEventEvaluationText;
            break;
          default:
            break;
        }
        e.monitoringEventUnits = evaluationMethod.unit;
        if (
          e.monitoringEventUrl
          && !e.monitoringEventUrl.toLowerCase().startsWith('http://')
          && !e.monitoringEventUrl.toLowerCase().startsWith('https://')
        ) {
          e.monitoringEventUrl = `${window.location.protocol}//${e.monitoringEventUrl}`;
        }
      });

      this.projectTargets.forEach((e) => {
        e.monitoringEvents = this.projectAccomplishmentTargetMonitoringEvents.filter(
          (monitoringEvent) => monitoringEvent.projectAccomplishmentTargetId === e.targetId,
        );
      });

    },
    onOutcomeSummaryEditClick() {
      this.initializeOutcomeSummaryFormModel();
      this.outcomeSummaryFormSchema = this.getProjectOutcomesSchema();
      this.$bvModal.show('outcomeSummaryEditModal');
    },
    onProjectTargetMonitoringEventAddClick(projectTarget) {

      this.initializeProjectTargetMonitoringEventAddModel(projectTarget);

      this.projectTargetMonitoringEventAddModelEvaluationMethods = [];
      console.log('this.projectAccomplishmentTargetMonitoringEventEvaluationMethods: ', this.projectAccomplishmentTargetMonitoringEventEvaluationMethods);
      this.projectTargetMonitoringEventAddModelEvaluationMethods = this.projectAccomplishmentTargetMonitoringEventEvaluationMethods.sort(
        (a, b) => a.name.localeCompare(b.name),
      );

      this.projectTargetMonitoringEventAddSchema = this.getProjectTargetMonitoringEventAddSchema();

      this.$bvModal.show('projectTargetMonitoringEventAddModal');

    },
    onProjectTargetMonitoringEventAddFormValidated(errors) {
      console.warn('Enter onProjectTargetMonitoringEventAddFormValidated()');

      console.warn('errors: ', errors);

      console.warn('this.projectTargetMonitoringEventAddModel: ', this.projectTargetMonitoringEventAddModel);

      this.projectTargetMonitoringEventAddModelValid = true;

      if (this.projectTargetMonitoringEventAddModel.monitoringEventDate > new Date()) {
        console.warn('invalid monitoringEventDate');
        this.projectTargetMonitoringEventAddModelValid = false;
      }

      if (this.projectTargetMonitoringEventAddModel.monitoringEventEvaluationMethod === null) {
        console.warn('invalid monitoringEventEvaluationMethod');
        this.projectTargetMonitoringEventAddModelValid = false;
      }

      if (this.projectTargetMonitoringEventAddModel.monitoringEventEvaluationMethodType === 'Bit') {
        if (this.projectTargetMonitoringEventAddModel.monitoringEventEvaluationBit === null) {
          console.warn('invalid monitoringEventEvaluationBit');
          this.projectTargetMonitoringEventAddModelValid = false;
        }
      }

      if (this.projectTargetMonitoringEventAddModel.monitoringEventEvaluationMethodType === 'Number') {
        console.warn('monitoringEventEvaluationNumber: ', this.projectTargetMonitoringEventAddModel.monitoringEventEvaluationNumber);
        if (Number.isNaN(this.projectTargetMonitoringEventAddModel.monitoringEventEvaluationNumber)) {
          console.warn('invalid monitoringEventEvaluationNumber');
          this.projectTargetMonitoringEventAddModelValid = false;
        } else if (this.projectTargetMonitoringEventAddModel.monitoringEventEvaluationNumber.toString().split('.').length === 2
          && this.projectTargetMonitoringEventAddModel.monitoringEventEvaluationNumber.toString().split('.')[1].length > 2
        ) {
          console.warn('invalid monitoringEventEvaluationNumber');
          this.projectTargetMonitoringEventAddModelValid = false;
        }
      }

      if (this.projectTargetMonitoringEventAddModel.monitoringEventEvaluationMethodType === 'Text') {
        if (!this.projectTargetMonitoringEventAddModel.monitoringEventEvaluationText
            || this.projectTargetMonitoringEventAddModel.monitoringEventEvaluationText == null
            || this.projectTargetMonitoringEventAddModel.monitoringEventEvaluationText.length < 2
            || this.projectTargetMonitoringEventAddModel.monitoringEventEvaluationText.length > 100
        ) {
          console.warn('invalid monitoringEventEvaluationText');
          this.projectTargetMonitoringEventAddModelValid = false;
        }
      }

      if (this.projectTargetMonitoringEventAddModel.monitoringEventUrl && this.projectTargetMonitoringEventAddModel.monitoringEventUrl.length > 150) {
        console.warn('invalid monitoringEventUrl');
        this.projectTargetMonitoringEventAddModelValid = false;
      }

      if (errors && errors.length > 0) {
        console.warn('errors: ', errors);
        this.projectTargetMonitoringEventAddModelValid = false;
      }

      console.warn('Exit onProjectTargetMonitoringEventAddFormValidated()');
    },
    onProjectTargetMonitoringEventAddFormModelUpdated(fieldValue, fieldName) {
      if (fieldName === 'monitoringEventEvaluationMethod') {
        if (fieldValue !== null) {
          const monitoringEventEvaluationMethod = this.projectAccomplishmentTargetMonitoringEventEvaluationMethods.find((e) => e.id === fieldValue.id);
          this.projectTargetMonitoringEventAddModel.monitoringEventEvaluationMethodType = monitoringEventEvaluationMethod.type;
          this.projectTargetMonitoringEventAddModel.monitoringEventEvaluationMethodUnit = monitoringEventEvaluationMethod.unit;
        }
        this.projectTargetMonitoringEventAddModel.monitoringEventEvaluationBit = null;
        this.projectTargetMonitoringEventAddModel.monitoringEventEvaluationNumber = 0;
        this.projectTargetMonitoringEventAddModel.monitoringEventEvaluationText = null;
      }
    },
    onProjectTargetMonitoringEventDeleteClick(monitoringEvent) {
      this.projectTargetMonitoringEventDeleteModel = monitoringEvent;
    },
    async onProjectTargetMonitoringEventDeleteOkClick() {
      this.formLoading = true;

      await this.$store.dispatch('Project/deleteProjectAccomplishmentTargetMonitoringEvent', this.projectTargetMonitoringEventDeleteModel.id);
      await this.loadProjectTargets();

      this.formLoading = false;
    },
    async saveOutcomeSummary() {
      this.formLoading = true;

      await this.$store.dispatch('Project/setProjectOutcomeSummary', this.outcomeSummaryFormModel);
      this.project.outcomeSummary = this.outcomeSummaryFormModel.outcomeSummary;

      this.showOutcomeSummarySaveSuccessAlert();

      this.formLoading = false;
    },
    async saveProjectTargetMonitoringEventAddModel() {
      this.formLoading = true;

      this.projectTargetMonitoringEventAddModel.monitoringEventEvaluationMethodId = this.projectTargetMonitoringEventAddModel.monitoringEventEvaluationMethod.id;

      if (this.projectTargetMonitoringEventAddModel.monitoringEventEvaluationMethodType === 'Bit') {
        this.projectTargetMonitoringEventAddModel.monitoringEventEvaluationNumber = null;
        this.projectTargetMonitoringEventAddModel.monitoringEventEvaluationText = null;
      }

      if (this.projectTargetMonitoringEventAddModel.monitoringEventEvaluationMethodType === 'Number') {
        this.projectTargetMonitoringEventAddModel.monitoringEventEvaluationBit = null;
        this.projectTargetMonitoringEventAddModel.monitoringEventEvaluationText = null;
      }

      if (this.projectTargetMonitoringEventAddModel.monitoringEventEvaluationMethodType === 'Text') {
        this.projectTargetMonitoringEventAddModel.monitoringEventEvaluationBit = null;
        this.projectTargetMonitoringEventAddModel.monitoringEventEvaluationNumber = null;
      }

      await this.$store.dispatch('Project/setProjectAccomplishmentTargetMonitoringEvent', this.projectTargetMonitoringEventAddModel);
      await this.loadProjectTargets();

      this.formLoading = false;
    },
    showOutcomeSummarySaveSuccessAlert() {
      this.dismissOutcomeSummarySaveSuccessAlert = 5;
    },
  },
};
</script>
